import type {
  InvoiceMatch,
  InvoiceMatchCandidates,
  InvoiceMatchLine,
  InvoiceMatchStatus,
  MatchLineStatusValue,
} from "@station/contracts";
import { Decimal, Money } from "@station/core";
import type { Tone } from "@station/ui";

/**
 * Üçlü eşleştirme ekranlarının saf mantığı (Prompt 16 — web, aşama B; ADR 0016 §9–10): aday seçimi → gövde, fark
 * raporu özeti ve eylemler. Farkı SUNUCU hesaplar (toleranslar eşleşmede dondurulur); ekran satırlardan yeniden
 * toplayıp başlıkla karşılaştırır — tutarsızlık (olmamalı) sessizce yutulmaz, uyarı olur.
 */

export const INVOICE_MATCH_STATUS_TONE: Record<InvoiceMatchStatus, Tone> = {
  MATCHED: "success",
  PENDING_APPROVAL: "warning",
  APPROVED: "success",
  UNMATCHED: "neutral",
};

export const MATCH_LINE_STATUS_TONE: Record<MatchLineStatusValue, Tone> = {
  OK: "success",
  QUANTITY_DIFFERENCE: "warning",
  PRICE_DIFFERENCE: "warning",
  QUANTITY_AND_PRICE_DIFFERENCE: "danger",
  NOT_RECEIVED: "danger",
  NOT_INVOICED: "warning",
  NOT_ORDERED: "warning",
};

/** Eşleşme geri alınabilir mi (onay bekleyen de geri alınabilir: bekleyen talep geri çekilir). */
export function matchActions(match: Pick<InvoiceMatch, "status">): { unmatch: boolean } {
  return { unmatch: match.status !== "UNMATCHED" };
}

/**
 * Faturaya bağlı irsaliye numaraları (e-İrsaliye / kâğıt irsaliye bağları) ile aynı irsaliye no'lu mal kabuller
 * ÖNERİLİR: seçim bununla başlar, kullanıcı değiştirebilir. Bağ yoksa hiçbir kabul önceden seçilmez.
 */
export function suggestedReceiptIds(
  invoice: Pick<InvoiceMatchCandidates["invoices"][number], "despatchNos">,
  receipts: readonly Pick<InvoiceMatchCandidates["receipts"][number], "receiptId" | "despatchNo">[],
): string[] {
  const numbers = new Set(invoice.despatchNos);
  return receipts
    .filter((receipt) => receipt.despatchNo !== null && numbers.has(receipt.despatchNo))
    .map((receipt) => receipt.receiptId);
}

export type MatchSelectionProblem = "invoice" | "receipts";

export function matchBody(
  invoiceId: string | null,
  receiptIds: readonly string[],
): { body: { invoiceId: string; receiptIds: string[] }; problems: MatchSelectionProblem[] } {
  const problems: MatchSelectionProblem[] = [];
  if (!invoiceId) problems.push("invoice");
  if (receiptIds.length === 0) problems.push("receipts");
  return { body: { invoiceId: invoiceId ?? "", receiptIds: [...new Set(receiptIds)] }, problems };
}

/** Seçilen kabullerin tutarı ile faturanın neti arasındaki ön fark (kuruş; + = fatura fazla) — yalnızca bilgi. */
export function selectionPreview(
  invoice: Pick<InvoiceMatchCandidates["invoices"][number], "netAmount"> | null,
  receipts: readonly Pick<InvoiceMatchCandidates["receipts"][number], "netAmount">[],
): { receiptsTotal: string; difference: string | null } {
  const receiptsTotal = Money.sum(receipts.map((receipt) => Money.fromMinor(receipt.netAmount)));
  return {
    receiptsTotal: receiptsTotal.toJSON(),
    difference: invoice ? Money.fromMinor(invoice.netAmount).sub(receiptsTotal).toJSON() : null,
  };
}

/** Satırın adı: ürün yoksa (nakliye, hizmet bedeli…) fatura satırının açıklaması. */
export function matchLineLabel(line: Pick<InvoiceMatchLine, "productName" | "description">): string {
  return line.productName ?? line.description ?? "—";
}

export function isDifferenceLine(line: Pick<InvoiceMatchLine, "status">): boolean {
  return line.status !== "OK";
}

/**
 * Fark raporu özeti: miktar toplamları, fark satırı sayısı ve Σ tutar farkı — satırlardan yeniden toplanır ve
 * başlıkla (`amountDifference`, `differenceLineCount`) karşılaştırılır.
 */
export function matchReportSummary(
  match: Pick<InvoiceMatch, "lines" | "amountDifference" | "differenceLineCount">,
): {
  ordered: string;
  received: string;
  invoiced: string;
  differenceLines: number;
  notOrderedLines: number;
  amountDifference: string;
  consistent: boolean;
} {
  let ordered = Decimal.from(0);
  let received = Decimal.from(0);
  let invoiced = Decimal.from(0);
  let amount = Money.ZERO;
  let differenceLines = 0;
  let notOrderedLines = 0;
  for (const line of match.lines) {
    if (line.orderedQuantity !== null) ordered = ordered.add(line.orderedQuantity);
    received = received.add(line.receivedQuantity);
    invoiced = invoiced.add(line.invoicedQuantity);
    amount = amount.add(Money.fromMinor(line.amountDifference));
    if (isDifferenceLine(line)) differenceLines += 1;
    if (line.status === "NOT_ORDERED") notOrderedLines += 1;
  }
  return {
    ordered: ordered.toFixed(3),
    received: received.toFixed(3),
    invoiced: invoiced.toFixed(3),
    differenceLines,
    notOrderedLines,
    amountDifference: match.amountDifference,
    consistent:
      amount.eq(Money.fromMinor(match.amountDifference)) && differenceLines === match.differenceLineCount,
  };
}

/**
 * Tolerans metni (eşleşmede dondurulan değerler): "Miktar %0 · Fiyat %1 · Tutar 1,00 ₺". Yüzdeler Türkiye
 * biçiminde, gereksiz sıfırsız.
 */
export function tolerancesText(tolerances: InvoiceMatch["tolerances"]): {
  quantity: string;
  price: string;
  amount: string;
} {
  const percent = (value: string) => {
    const decimal = Decimal.from(value);
    const scale = (decimal.toString().split(".")[1] ?? "").replace(/0+$/, "").length;
    return `%${decimal.formatTr(scale)}`;
  };
  return {
    quantity: percent(tolerances.quantityPercent),
    price: percent(tolerances.pricePercent),
    amount: Money.fromMinor(tolerances.amountAbsolute).formatTr(),
  };
}
